import { useAuthContext } from "@acto/web-common/auth";
import { useLocale } from "@acto/web-common/locales";
import { useCallback, useEffect, useRef, useState } from "react";
import { buildEventHeaders, isAbortError, readEventStream } from "@acto/taskpane/core/utils/sse";
import { useSessionTelemetry } from "@acto/taskpane/core/telemetry/SessionTelemetryProvider";
import { fetchSummaryResult, isSummaryResult, requestSummary, SummaryOperationError } from "./summaryApi";
import type {
  SummaryGenerationStatus,
  SummaryResultSource,
  SummaryResult,
  FinalEventPayload,
  CreateSummaryResponse,
  UseSummaryGenerationOptions,
  UseSummaryGenerationResult,
} from "./types";

const API_BASE_URL = import.meta.env.VITE_INTELLIGENCE_LAYER_API_URL;

const RESULT_POLL_INTERVAL_MS = 2000;
// The summary job runs under a 60s soft timeout and reports FAILED when it expires, so the poll
// outlives the job and lets the server settle the operation rather than guessing at a cutoff.
const RESULT_POLL_DEADLINE_MS = 90_000;

interface ActiveOperation {
  opId: string;
  abortController: AbortController;
}

export function useSummaryGeneration(options: UseSummaryGenerationOptions = {}): UseSummaryGenerationResult {
  const { accessToken } = useAuthContext();
  const { locale } = useLocale();
  const telemetry = useSessionTelemetry();
  const { onComplete, onRefetchNotes } = options;

  const [status, setStatus] = useState<SummaryGenerationStatus>("idle");
  const [error, setError] = useState<Error | null>(null);
  const [resultSource, setResultSource] = useState<SummaryResultSource>("none");
  const [result, setResult] = useState<SummaryResult | null>(null);
  const [operationId, setOperationId] = useState<string | null>(null);

  // Every failure lands here, so the handled-error report cannot drift from the failed status.
  const fail = useCallback(
    (context: string, cause: unknown) => {
      const err = cause instanceof Error ? cause : new Error(String(cause));
      console.error(`[SummaryGeneration] ${context}:`, err);
      void telemetry?.recordError("handled", err, { feature: "meeting-recap" });
      setStatus("failed");
      setError(err);
    },
    [telemetry],
  );

  const activeOperationsRef = useRef<Map<string, ActiveOperation>>(new Map());
  const streamAbortRef = useRef<AbortController | null>(null);

  const cleanupStream = useCallback(() => {
    if (streamAbortRef.current) {
      streamAbortRef.current.abort();
      streamAbortRef.current = null;
    }
  }, []);

  const cleanupOperation = useCallback((noteId: string) => {
    const operation = activeOperationsRef.current.get(noteId);
    if (operation) {
      operation.abortController.abort();
      activeOperationsRef.current.delete(noteId);
    }
  }, []);

  // Both the stream's final event and the result poll settle through here, so a summary that
  // arrives either way lands in the same state.
  const settleSucceeded = useCallback(
    (noteId: string, summaryResult: SummaryResult) => {
      cleanupOperation(noteId);
      setStatus("succeeded");
      setResultSource("final");
      setResult(summaryResult);
      setError(null);
      onRefetchNotes?.();
      onComplete?.();
    },
    [cleanupOperation, onRefetchNotes, onComplete],
  );

  /**
   * The job outlives its stream: it keeps running and persists the summary, so a dropped
   * connection is recoverable by asking for the result. Holds a pending status throughout.
   *
   * A transport error counts as "still running" here, unlike the terminal states the endpoint
   * reports: the dropped stream and a failing poll share one cause, and the connection is
   * usually back inside the window.
   */
  const pollForResult = useCallback(
    async (opId: string, signal: AbortSignal): Promise<SummaryResult | undefined> => {
      if (!accessToken || signal.aborted) return undefined;
      setStatus("accepted");

      const deadline = Date.now() + RESULT_POLL_DEADLINE_MS;
      for (let attempt = 0; Date.now() < deadline; attempt += 1) {
        if (attempt > 0) {
          await new Promise((resolve) => setTimeout(resolve, RESULT_POLL_INTERVAL_MS));
        }
        if (signal.aborted) return undefined;

        try {
          const polled = await fetchSummaryResult(opId, accessToken, locale, signal);
          if (polled) return polled;
        } catch (err) {
          if (isAbortError(err)) return undefined;
          if (err instanceof SummaryOperationError) throw err;
        }
      }

      return undefined;
    },
    [accessToken, locale],
  );

  const recoverResult = useCallback(
    async (noteId: string, opId: string, signal: AbortSignal, cause: unknown) => {
      // A reset or a newer submission for this note supersedes the poll. Settling either way
      // then would overwrite the current status and abort the newer operation.
      const isCurrent = () => !signal.aborted && activeOperationsRef.current.get(noteId)?.opId === opId;

      try {
        const polled = await pollForResult(opId, signal);
        if (!isCurrent()) return;
        if (polled) {
          settleSucceeded(noteId, polled);
          return;
        }
        cleanupOperation(noteId);
        fail(`Lost the summary stream for note ${noteId}`, cause);
      } catch (err) {
        if (!isCurrent()) return;
        // The operation's own failure, reported as that rather than as the lost stream.
        cleanupOperation(noteId);
        fail(`Summary operation failed for note ${noteId}`, err);
      }
    },
    [cleanupOperation, fail, pollForResult, settleSucceeded],
  );

  useEffect(() => {
    return () => {
      cleanupStream();
      activeOperationsRef.current.forEach((op) => {
        op.abortController.abort();
      });
      activeOperationsRef.current.clear();
    };
  }, [cleanupStream]);

  const openStream = useCallback(
    async (noteId: string, opId: string) => {
      if (!accessToken) return;
      if (!API_BASE_URL) {
        fail("Missing VITE_INTELLIGENCE_LAYER_API_URL", new Error("Missing VITE_INTELLIGENCE_LAYER_API_URL"));
        return;
      }

      cleanupOperation(noteId);

      const controller = new AbortController();
      activeOperationsRef.current.set(noteId, { opId, abortController: controller });
      streamAbortRef.current = controller;

      setStatus("streaming");

      try {
        const response = await fetch(`${API_BASE_URL}/meeting_recap/summarize/operations/${opId}/events`, {
          method: "GET",
          // No Last-Event-ID: a lost stream is recovered by polling the result, not by resuming.
          headers: buildEventHeaders(accessToken, null, locale),
          signal: controller.signal,
        });

        if (!response.ok) {
          const message = await response.text().catch(() => "");
          throw new Error(message || `Failed to connect to summary stream (${response.status})`);
        }

        if (!response.body) {
          throw new Error("Missing response body for summary stream");
        }

        let receivedFinalEvent = false;

        await readEventStream(response, (event) => {
          if (event.event !== "final") {
            return;
          }

          // Claimed before parsing, so a malformed final event is reported once as a parse
          // failure rather than also as a stream that ended without a result.
          if (receivedFinalEvent) {
            return;
          }
          receivedFinalEvent = true;

          try {
            const parsed = JSON.parse(event.data) as FinalEventPayload;
            const { data: eventData } = parsed;

            if (eventData.state === "SUCCEEDED") {
              if (isSummaryResult(eventData.result)) {
                settleSucceeded(noteId, eventData.result);
              } else {
                // Settling this as an empty success would report completion and then fall back
                // to the note's earlier summary, the state this guard exists to prevent.
                cleanupOperation(noteId);
                fail(
                  `Summary result payload was not understood for note ${noteId}`,
                  new Error("Summary result payload was not understood"),
                );
              }
            } else if (eventData.state === "FAILED") {
              cleanupOperation(noteId);
              fail(`Operation failed for note ${noteId}`, new Error(eventData.error || "Summary generation failed"));
            }
          } catch (err) {
            cleanupOperation(noteId);
            fail(`Failed to parse stream event for note ${noteId}`, err);
          }
        });

        // A stream can also end without its final event, e.g. a proxy closing an idle
        // connection. The job is unaffected, so recover it the same way as a failed read.
        if (!receivedFinalEvent) {
          await recoverResult(noteId, opId, controller.signal, new Error("Summary stream ended without a result"));
        }
      } catch (err) {
        if (isAbortError(err)) {
          return;
        }
        await recoverResult(noteId, opId, controller.signal, err);
      } finally {
        if (streamAbortRef.current === controller) {
          streamAbortRef.current = null;
        }
      }
    },
    [accessToken, cleanupOperation, fail, locale, recoverResult, settleSucceeded],
  );

  const handleResponse = useCallback(
    (noteId: string, response: CreateSummaryResponse) => {
      if (response.status === "completed") {
        setStatus("succeeded");
        setResultSource("cached");
        setResult(response.result);
        setOperationId(null);
        setError(null);
        onRefetchNotes?.();
        onComplete?.();
        return;
      }

      const existingOp = activeOperationsRef.current.get(noteId);
      if (existingOp && existingOp.opId === response.operation.id) {
        return;
      }

      setOperationId(response.operation.id);
      setStatus("accepted");

      if (response.cached_result) {
        setResultSource("cached");
        setResult(response.cached_result);
        onRefetchNotes?.();
      }

      if (response.operation.state === "SUCCEEDED") {
        setStatus("succeeded");
        setResultSource("final");
        setError(null);
        onRefetchNotes?.();
        onComplete?.();
        return;
      }

      if (response.operation.state === "FAILED") {
        fail(`Operation failed for note ${noteId}`, new Error("Summary generation failed"));
        return;
      }

      void openStream(noteId, response.operation.id);
    },
    [fail, onRefetchNotes, onComplete, openStream],
  );

  const triggerSummary = useCallback(
    async (noteId: string) => {
      if (!accessToken) {
        console.warn("[SummaryGeneration] No access token available");
        return;
      }

      if (!noteId) {
        console.warn("[SummaryGeneration] No note ID provided");
        return;
      }

      cleanupStream();
      cleanupOperation(noteId);
      setStatus("submitting");
      setError(null);
      setOperationId(null);
      setResultSource("none");
      setResult(null);

      try {
        handleResponse(noteId, await requestSummary(noteId, accessToken, locale));
      } catch (err) {
        fail("Failed to trigger summary", err);
      }
    },
    [accessToken, locale, cleanupStream, cleanupOperation, fail, handleResponse],
  );

  const isProcessing = useCallback((noteId: string): boolean => {
    return activeOperationsRef.current.has(noteId);
  }, []);

  const reset = useCallback(() => {
    cleanupStream();
    activeOperationsRef.current.forEach((op) => {
      op.abortController.abort();
    });
    activeOperationsRef.current.clear();
    setStatus("idle");
    setError(null);
    setResultSource("none");
    setResult(null);
    setOperationId(null);
  }, [cleanupStream]);

  const isPending = status === "submitting" || status === "accepted" || status === "streaming";

  return {
    status,
    error,
    resultSource,
    result,
    operationId,
    isPending,
    isStreaming: status === "streaming",
    triggerSummary,
    isProcessing,
    reset,
  };
}
